/* eslint-disable */
/* eslint-disable no-unused-vars */
/* eslint-disable implicit-arrow-linebreak */
import ReactModal from 'react-modal';
import React, { useEffect, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import { DefaultTable } from 'src/components';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { IBenefitListTable } from 'src/interfaces';
import { useMutation, useQuery } from '@tanstack/react-query';
import BenefitsService from 'src/services/benefits.service';
import { CustomCell, TooltipBoxTable } from 'src/components/tooltip-box-table';
import { Container } from './styles';
import { AlertAdapter, CommonInput, RegularButton } from 'pg-ui-library';
import { MenuItem, Select } from '@mui/material';
import BenefitsMasterService from 'src/services/benefits-master.service';
import chunk from 'lodash/chunk';

export const benefitsListInitialColumns: TableColumn<IBenefitListTable>[] = [
  {
    name: 'Benefit Id',
    selector: (row: any) => row.benefitID || '',
    sortField: 'benefitID',
    width: '180px',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Plan',
    selector: row => row.plan || '',
    sortField: 'plan',
    width: '180px',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Plan Description',
    selector: row => row.planDescription || '',
    sortField: 'planDescription',
    width: '180px',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'General Category',
    selector: (row: any) => row.generalCategory || '',
    sortField: 'generalCategory',
    width: '180px',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Events',
    selector: (row: any) => row.whenEmployeeCanEnroll || '',
    sortField: 'whenEmployeeCanEnroll',
    width: '220px',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Type',
    selector: row => row.type || '',
    sortField: 'type',
    width: '180px',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Rates',
    selector: row => row.rates.value,
    cell: ({ rates }) => (
      <CustomCell value={rates.value}>
        <TooltipBoxTable title="Rates" columns={rates.columns} rows={rates.rows} />
      </CustomCell>
    ),
    wrap: true,
  },
];

export const BenefitsListTable: React.FC<{
  searchTerm: string;
  columns: TableColumn<IBenefitListTable>[];
}> = ({ searchTerm, columns }) => {
  const [data, setData] = useState<IBenefitListTable[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState('plan');
  const [sortDirection, setSortDirection] = useState<any>('asc');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<IBenefitListTable | null>(null);
  const [formData, setFormData] = useState<any>({});

  const companyId = useTypedSelector(state => state.config.data?.config?.selectedCompanyId) || '';

  useEffect(() => {
    if (selectedRow) {
      setFormData({ ...selectedRow });
    }
  }, [selectedRow]);

  const {
    data: benefitsItems,
    refetch,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['benefits', page, limit, searchTerm, sortBy, sortDirection],
    queryFn: () =>
      BenefitsService.getBenefits({
        page,
        companyId,
        limit,
        search: searchTerm,
        sortBy,
        sortDirection,
      }),
    enabled: true,
  });

  useEffect(() => {
    refetch();
  }, [searchTerm, page, limit, sortBy, sortDirection, modalIsOpen]);

  useEffect(() => {
    if (benefitsItems) {
      setData(benefitsItems.items || []);
      setTotalRows(benefitsItems.meta.totalItems || 0);
    }
  }, [benefitsItems]);

  const handlePageChange = (nPage: number) => setPage(nPage);
  const handlePerRowsChange = (newLimit: number, nPage: number) => {
    setLimit(newLimit);
    setPage(nPage);
  };
  const handleSort = (column: TableColumn<IBenefitListTable>, direction: SortOrder) => {
    setSortBy(column.sortField as string);
    setSortDirection(direction);
  };
  const handleRowClick = (row: IBenefitListTable) => {
    setSelectedRow(row);
    setModalIsOpen(true);
  };

  const computeChWidth = (items: string[]) => {
    const maxLen = Math.max(...items.map(i => i.length), 0);
    return `${maxLen + 2}ch`;
  };

  const dropdownOptions = {
    allowValueChangeAtAnytime: ['Yes', 'No'],
    necessaryToProvideBeneficiariesInformation: ['Yes', 'No'],
    postAndPreTax: ['Pre tax', 'Post tax'],
    costCalculationMethod: ['Fixed', 'Table', 'Limited', 'Free'],
    timeLimitThatEmployeePaymentApplies: ['Semi Monthly', 'Annual'],
    employeeChoosesPayment: ['Yes', 'No'],
    mandatory: ['Yes', 'No'],
    ignoreSmokerFilter: ['Yes', 'No'],
    whichEmployee: ['Both', 'External', 'Internal', 'None'],
    generalCategory: ['Health & Well-being', 'Income Security', 'Retirement & Lifestyle'],
    onlyFullTime: ['Yes', 'No'],
    salaryRange: ['Yes', 'No'],
    dependents: ['EE', 'EE+C', 'EE+SP', 'FAM'],
    hideCostFromEmployees: ['Yes', 'No'],
  };

  const multiSelectOptions = {
    whenEmployeeCanEnroll: ['OE', 'QLE', 'NH', 'UE'],
    availableOnlyForThisStates: [
      'AL',
      'AK',
      'AZ',
      'AR',
      'CA',
      'CO',
      'CT',
      'DE',
      'FL',
      'GA',
      'HI',
      'ID',
      'IL',
      'IN',
      'IA',
      'KS',
      'KY',
      'LA',
      'ME',
      'MD',
      'MA',
      'MI',
      'MN',
      'MS',
      'MO',
      'MT',
      'NE',
      'NV',
      'NH',
      'NJ',
      'NM',
      'NY',
      'NC',
      'ND',
      'OH',
      'OK',
      'OR',
      'PA',
      'RI',
      'SC',
      'SD',
      'TN',
      'TX',
      'UT',
      'VT',
      'VA',
      'WA',
      'WV',
      'WI',
      'WY',
    ],
    availableSpendingAccountsForThisPlan: ['FSA', 'DCA', 'HSA'],
  };

  const formatDateForInput = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const pad = (num: number) => num.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
      date.getMinutes()
    )}`;
  };

  const mutation = useMutation(
    async (variables: any) => {
      await BenefitsMasterService.uploadBenefitsMasterByModal(variables);
    },
    {
      onSuccess: () => {
        AlertAdapter('Benefits Updated!', 'success');
        setModalIsOpen(false);
      },
      onError: () => {
        AlertAdapter('Fail on updating benefits!', 'error');
      },
    }
  );

  const filteredEntries = Object.entries(formData).filter(
    ([key]) => !['rates', 'createdAt', 'updatedAt', 'toRemove', 'id'].includes(key)
  );

  const groupedSections = chunk(filteredEntries, 12);

  return (
    <Container>
      <DefaultTable
        columns={columns}
        data={data}
        progressPending={isLoading || isFetching}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        onSort={handleSort}
        sortServer
        onRowClicked={handleRowClick}
      />

      <ReactModal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        ariaHideApp={false}
        style={{
          overlay: { backgroundColor: 'rgba(0, 0, 0, 0.6)' },
          content: {
            borderRadius: '12px',
            margin: 'auto',
            marginLeft: '100px',
            width: '90%',
            maxWidth: '1300px',
            maxHeight: '90%',
            overflow: 'auto',
            background: '#F9F9FB',
            padding: '2.5rem',
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          },
        }}
      >
        <h2>Edit Benefits</h2>

        {/* Wrapper vertical das seções */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
          {groupedSections.map((sectionEntries, sectionIndex) => (
            <div key={sectionIndex}>
              <h3 style={{ marginBottom: '1.5rem', color: '#333', marginLeft: '5px' }}>Section {sectionIndex + 1}</h3>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 2fr))',
                  gap: '3.5rem',
                  background: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                }}
              >
                {sectionEntries.map(([key, value]) => {
                  const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (str: string) => str.toUpperCase());
                  const isDropdown = key in dropdownOptions;
                  const isMultiSelect = key in multiSelectOptions;

                  const fieldWidth = isDropdown
                    ? computeChWidth((dropdownOptions as Record<string, string[]>)[key])
                    : isMultiSelect
                    ? computeChWidth((multiSelectOptions as Record<string, string[]>)[key])
                    : typeof value === 'boolean'
                    ? computeChWidth(['Yes', 'No'])
                    : undefined;

                  return (
                    <div key={key} style={{ display: 'flex', flexDirection: 'column' }}>
                      <label
                        style={{
                          fontWeight: 600,
                          color: '#555',
                          marginBottom: '0.5rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'block',
                        }}
                      >
                        {label}
                      </label>

                      {typeof value === 'boolean' ? (
                        <Select
                          value={value ? 'Yes' : 'No'}
                          onChange={e =>
                            setFormData((prev: Record<string, any>) => ({
                              ...prev,
                              [key]: e.target.value === 'Yes',
                            }))
                          }
                          displayEmpty
                          sx={{
                            height: '40px',
                            fontSize: '16px',
                            minWidth: fieldWidth,
                            '& .MuiSelect-select': { padding: '10px' },
                          }}
                        >
                          <MenuItem value="Yes">Yes</MenuItem>
                          <MenuItem value="No">No</MenuItem>
                        </Select>
                      ) : isDropdown ? (
                        <Select
                          value={value || ''}
                          onChange={e =>
                            setFormData((prev: Record<string, any>) => ({
                              ...prev,
                              [key]: e.target.value,
                            }))
                          }
                          displayEmpty
                          sx={{
                            height: '40px',
                            fontSize: '16px',
                            minWidth: fieldWidth,
                            '& .MuiSelect-select': { padding: '10px' },
                          }}
                        >
                          {(dropdownOptions as Record<string, string[]>)[key].map(option => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : isMultiSelect ? (
                        <Select
                          multiple
                          value={(value as string)?.split(';').filter(Boolean) || []}
                          onChange={e => {
                            const selected = e.target.value as string[];
                            setFormData((prev: Record<string, any>) => ({
                              ...prev,
                              [key]: selected.join(';'),
                            }));
                          }}
                          renderValue={selected => (selected as string[]).join(', ')}
                          sx={{
                            minWidth: fieldWidth,
                            fontSize: '16px',
                            '& .MuiSelect-select': { padding: '10px' },
                          }}
                        >
                          {(multiSelectOptions as Record<string, string[]>)[key].map(option => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      ) : key.toLowerCase().includes('date') ? (
                        <CommonInput
                          style={{ height: '40px', marginTop: '-7px' }}
                          type="datetime-local"
                          value={formatDateForInput(value as string)}
                          onChange={e =>
                            setFormData((prev: Record<string, any>) => ({
                              ...prev,
                              [key]: e.target.value ? new Date(e.target.value).toISOString() : null,
                            }))
                          }
                          label=""
                        />
                      ) : typeof value === 'number' ? (
                        <CommonInput
                          style={{ height: '40px', marginTop: '-7px' }}
                          type="number"
                          value={value ?? ''}
                          onChange={e =>
                            setFormData((prev: Record<string, any>) => ({
                              ...prev,
                              [key]: e.target.value ? parseFloat(e.target.value) : null,
                            }))
                          }
                          label=""
                        />
                      ) : (
                        <CommonInput
                          style={{ height: '40px', marginTop: '-7px' }}
                          type="text"
                          disabled={key === 'benefitID'}
                          value={(value as string) || ''}
                          onChange={e =>
                            setFormData((prev: Record<string, any>) => ({
                              ...prev,
                              [key]: e.target.value,
                            }))
                          }
                          label=""
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: '2rem',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '1rem',
          }}
        >
          <RegularButton
            onClick={() => setModalIsOpen(false)}
            style={{
              padding: '0.7rem 1.5rem',
              background: '#fff',
              color: '#5E3BEE',
              border: '#5E3BEE 1px solid',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </RegularButton>
          <RegularButton
            onClick={() => {
              mutation.mutate(formData);
            }}
            style={{
              padding: '0.7rem 1.5rem',
              background: '#5E3BEE',
              borderRadius: '6px',
              cursor: 'pointer',
              width: '150px',
              marginRight: '30px',
            }}
          >
            Save
          </RegularButton>
        </div>
      </ReactModal>
    </Container>
  );
};
